import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  KeyRound,
  Mail,
} from 'lucide-react';
import { useEmailAccount, type EmailAccount } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
  type EmailAccountFormData,
  type IncomingProtocolFormValue,
} from './config';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, Label, TextInput, Select, Switch } from '../../components/ui';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { useNotification } from '../../contexts/NotificationContext';
import OAuthConnectSection from './components/OAuthConnectSection';
import { SentFolderIdentitiesSection } from './components/SentFolderIdentitiesSection';
import { AccountSharesSection } from './components/AccountSharesSection';

type WizardStepId = 'basics' | 'protocol' | 'incoming' | 'outgoing' | 'sync' | 'sidebar' | 'sharing';

const WIZARD_STEP_LABELS: Record<WizardStepId, string> = {
  basics: 'Account',
  protocol: 'Protocol',
  incoming: 'Incoming',
  outgoing: 'Outgoing',
  sync: 'Sync',
  sidebar: 'Sidebar',
  sharing: 'Sharing',
};

const PROTOCOL_LABELS: Record<IncomingProtocolFormValue, string> = {
  imap: 'IMAP',
  oauth2: 'OAuth2',
  pop3: 'POP3',
};

const PROTOCOL_OPTIONS: {
  value: IncomingProtocolFormValue;
  label: string;
  hint: string;
  detail: string;
  icon: typeof Mail;
}[] = [
  {
    value: 'imap',
    label: 'IMAP',
    hint: 'Recommended',
    detail: 'Keeps folders in sync on the server. Best for most providers.',
    icon: Mail,
  },
  {
    value: 'oauth2',
    label: 'OAuth2',
    hint: 'Google / Microsoft',
    detail: 'Sign in with Google or Microsoft. Uses IMAP/SMTP with XOAUTH2.',
    icon: KeyRound,
  },
  {
    value: 'pop3',
    label: 'POP3',
    hint: 'Download only',
    detail: 'Downloads mail to the app. Folders are not kept in sync.',
    icon: Download,
  },
];

function buildWizardSteps(protocol: IncomingProtocolFormValue): WizardStepId[] {
  const steps: WizardStepId[] = ['basics', 'protocol', 'incoming'];
  if (protocol !== 'oauth2') steps.push('outgoing');
  steps.push('sync', 'sidebar', 'sharing');
  return steps;
}

function protocolBadgeClass(protocol: IncomingProtocolFormValue): string {
  switch (protocol) {
    case 'oauth2':
      return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300';
    case 'pop3':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
    default:
      return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300';
  }
}

function applyOAuthDefaults(form: EmailAccountFormData): EmailAccountFormData {
  return {
    ...form,
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    imapSecure: true,
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpSecure: true,
    imapMirrorMode: 'full_mailbox',
  };
}

/** Decoy fields absorb browser/password-manager autofill away from real inputs. */
function AutofillHoneypot() {
  return (
    <div aria-hidden="true" className="absolute opacity-0 pointer-events-none h-0 w-0 overflow-hidden">
      <input tabIndex={-1} autoComplete="username" name="prevent_autofill_username" defaultValue="" readOnly />
      <input tabIndex={-1} type="password" autoComplete="current-password" name="prevent_autofill_password" defaultValue="" readOnly />
    </div>
  );
}

function validateWizardStep(step: WizardStepId, form: EmailAccountFormData): boolean {
  switch (step) {
    case 'basics': {
      const email = (form.emailAddress ?? '').trim();
      return email.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
    case 'protocol':
      return true;
    case 'incoming':
      if (form.incomingProtocol === 'oauth2') {
        return Boolean(form.imapHost?.trim()) && Boolean(form.smtpHost?.trim());
      }
      if (form.incomingProtocol === 'pop3') {
        return Boolean(form.pop3Host?.trim());
      }
      return Boolean(form.imapHost?.trim());
    case 'outgoing':
      return Boolean(form.smtpHost?.trim()) && Boolean(form.username?.trim());
    case 'sync':
    case 'sidebar':
    case 'sharing':
      return true;
    default:
      return true;
  }
}

function FieldGroup({
  title,
  description,
  children,
  colors,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className={cn('text-sm font-semibold', colors.text)}>{title}</h2>
        {description && <p className={cn('text-xs mt-0.5', colors.textMuted)}>{description}</p>}
      </div>
      {children}
    </section>
  );
}

function ProtocolPicker({
  value,
  onChange,
  colors,
}: {
  value: IncomingProtocolFormValue;
  onChange: (protocol: IncomingProtocolFormValue) => void;
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <div
      className={cn('grid grid-cols-1 sm:grid-cols-3 rounded-xl border overflow-hidden', colors.border)}
      role="radiogroup"
      aria-label="Incoming mail protocol"
    >
      {PROTOCOL_OPTIONS.map((opt, index) => {
        const selected = value === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              'relative flex flex-col items-start gap-3 p-4 text-left transition-colors min-h-[7.5rem]',
              index < PROTOCOL_OPTIONS.length - 1 && 'sm:border-r',
              colors.border,
              selected
                ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                : cn(colors.surface, colors.text, 'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]'),
            )}
          >
            <div className="flex w-full items-start justify-between gap-2">
              <span
                className={cn(
                  'inline-flex items-center justify-center w-9 h-9 rounded-lg',
                  selected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
                )}
              >
                <Icon className="w-4 h-4" aria-hidden />
              </span>
              {selected && <Check className="w-4 h-4 shrink-0 opacity-90" aria-hidden />}
            </div>
            <span className="min-w-0">
              <span className={cn('block text-sm font-semibold', selected ? 'text-white' : colors.text)}>
                {opt.label}
              </span>
              <span
                className={cn(
                  'block text-[11px] font-medium uppercase tracking-wide mt-0.5',
                  selected ? 'text-white/75' : colors.textMuted,
                )}
              >
                {opt.hint}
              </span>
              <span className={cn('block text-xs mt-1.5 leading-snug', selected ? 'text-white/85' : colors.textMuted)}>
                {opt.detail}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function WizardStepper({
  steps,
  currentIndex,
  onStepClick,
  colors,
}: {
  steps: WizardStepId[];
  currentIndex: number;
  onStepClick: (index: number) => void;
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <nav aria-label="Email account wizard progress" className={cn('border-b px-4 py-4 sm:px-6', colors.border)}>
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 sm:gap-x-3">
        {steps.map((stepId, index) => {
          const isComplete = index < currentIndex;
          const isCurrent = index === currentIndex;
          const clickable = index < currentIndex;
          return (
            <li key={stepId} className="flex items-center shrink-0">
              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onStepClick(index)}
                className={cn(
                  'flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 text-left transition-colors',
                  isCurrent && 'bg-indigo-50 dark:bg-indigo-950/40',
                  clickable && 'cursor-pointer hover:bg-black/[0.04] dark:hover:bg-white/[0.04]',
                  !clickable && !isCurrent && 'cursor-default',
                )}
              >
                <span
                  className={cn(
                    'flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold tabular-nums shrink-0',
                    isComplete && 'bg-indigo-600 text-white dark:bg-indigo-500',
                    isCurrent && 'bg-indigo-600 text-white ring-2 ring-indigo-300 dark:ring-indigo-700 dark:bg-indigo-500',
                    !isComplete && !isCurrent && cn('border', colors.border, colors.textMuted),
                  )}
                >
                  {isComplete ? <Check className="w-3.5 h-3.5" aria-hidden /> : index + 1}
                </span>
                <span
                  className={cn(
                    'text-xs font-medium whitespace-nowrap',
                    isCurrent ? colors.text : colors.textMuted,
                  )}
                >
                  {WIZARD_STEP_LABELS[stepId]}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function SwitchField({
  label,
  description,
  checked,
  onChange,
  colors,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4 rounded-lg border p-3', colors.border)}>
      <div className="min-w-0">
        <p className={cn('text-sm font-medium', colors.text)}>{label}</p>
        {description && <p className={cn('text-xs mt-0.5', colors.textMuted)}>{description}</p>}
      </div>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
    </div>
  );
}

const Form: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { mutate: globalMutate } = useSWRConfig();

  const isEdit = /\/email-accounts\/\d+\/edit$/.test(location.pathname);
  const accountId = isEdit && id ? parseInt(id, 10) : 0;
  const { data: account, isLoading: loadingAccount, mutate } = useEmailAccount(accountId);

  const [form, setForm] = useState<EmailAccountFormData>(() => getInitialFormData());
  const [createdAccount, setCreatedAccount] = useState<EmailAccount | null>(null);
  const [saving, setSaving] = useState(false);
  const [wizardStepIndex, setWizardStepIndex] = useState(0);

  const protocol = form.incomingProtocol ?? 'imap';
  const wizardSteps = useMemo(() => buildWizardSteps(protocol), [protocol]);
  const sharingAccount = account ?? createdAccount;
  const currentWizardStep = wizardSteps[wizardStepIndex] ?? wizardSteps[0];
  const isLastWizardStep = wizardStepIndex >= wizardSteps.length - 1;
  const canProceedWizard =
    currentWizardStep === 'basics' || validateWizardStep(currentWizardStep, form);

  const hydratedAccountIdRef = useRef<number | null>(null);

  useEffect(() => {
    hydratedAccountIdRef.current = null;
  }, [accountId]);

  useEffect(() => {
    if (!account) return;
    if (hydratedAccountIdRef.current === account.id) return;
    hydratedAccountIdRef.current = account.id;
    setForm(mapItemToFormData(account));
  }, [account]);

  useEffect(() => {
    setWizardStepIndex((index) => Math.min(index, Math.max(0, wizardSteps.length - 1)));
  }, [wizardSteps.length]);

  const patchForm = useCallback((patch: Partial<EmailAccountFormData>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const setProtocol = useCallback((next: IncomingProtocolFormValue) => {
    setForm((prev) => (next === 'oauth2' ? applyOAuthDefaults({ ...prev, incomingProtocol: next }) : { ...prev, incomingProtocol: next }));
  }, []);

  const goWizardNext = useCallback(() => {
    const step = wizardSteps[wizardStepIndex] ?? 'basics';
    let nextForm = form;

    if (step === 'basics') {
      const emailFromDom =
        (document.getElementById('ea-email') as HTMLInputElement | null)?.value?.trim() ?? '';
      if (emailFromDom && emailFromDom !== (form.emailAddress ?? '').trim()) {
        nextForm = { ...form, emailAddress: emailFromDom };
        setForm(nextForm);
      }
      if (!validateWizardStep('basics', nextForm)) {
        showError('Enter a valid email address to continue.');
        return;
      }
    } else if (!validateWizardStep(step, form)) {
      showError('Complete the required fields on this step before continuing.');
      return;
    }

    requestAnimationFrame(() => {
      setWizardStepIndex((index) => Math.min(index + 1, wizardSteps.length - 1));
    });
  }, [wizardSteps, wizardStepIndex, form, showError]);

  const goWizardBack = useCallback(() => {
    setWizardStepIndex((index) => Math.max(index - 1, 0));
  }, []);

  const saveAccount = useCallback(async () => {
    if (!validateWizardStep('basics', form)) return;
    setSaving(true);
    try {
      if (isEdit && accountId) {
        await moduleConfig.form.update(accountId, form);
        await globalMutate((key: unknown) => typeof key === 'string' && key.startsWith('/email-accounts'));
        await mutate();
        showSuccess('Email account updated.');
        navigate(moduleConfig.module.route.detail(accountId));
      } else if (createdAccount) {
        navigate(moduleConfig.module.route.detail(createdAccount.id));
      } else {
        const created = await moduleConfig.form.create(form);
        await globalMutate((key: unknown) => typeof key === 'string' && key.startsWith('/email-accounts'));
        setCreatedAccount(created);
        setForm(mapItemToFormData(created));
        showSuccess('Email account created. You can share access below.');
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || moduleConfig.form.errorMessage);
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, accountId, createdAccount, globalMutate, mutate, navigate, showSuccess, showError]);

  const handleFormKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLastWizardStep) return;
    goWizardNext();
  };

  const showImapMirror = protocol === 'imap' || protocol === 'oauth2';
  const displayEmail = form.emailAddress.trim() || 'New account';
  const displayName = form.displayName?.trim();
  const finishButtonLabel = isEdit ? 'Save' : createdAccount ? 'Done' : 'Create';

  if (isEdit && loadingAccount && !account) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="large" message={moduleConfig.form.loadingText} />
      </div>
    );
  }

  if (isEdit && accountId && !account) {
    return (
      <div className={cn('max-w-lg mx-auto p-8 text-center rounded-xl border', colors.border, colors.surface)}>
        <p className={cn('font-medium', colors.text)}>Email account not found</p>
        <Link
          to={moduleConfig.module.route.list}
          className={cn('inline-flex items-center gap-1.5 mt-4 text-sm font-medium', colors.primaryFg)}
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          Back to list
        </Link>
      </div>
    );
  }

  return (
    <div className={cn('h-full flex flex-col min-h-0', colors.background)}>
      <form noValidate autoComplete="off" onSubmit={handleFormKeySubmit} className="relative flex flex-col flex-1 min-h-0">
        <AutofillHoneypot />
        <div
          className={cn(
            'sticky top-0 z-20 border-b backdrop-blur-md',
            colors.border,
            'bg-white/90 dark:bg-gray-950/90',
          )}
        >
          <div className="w-full px-4 sm:px-6 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <Link
                to={isEdit && accountId ? moduleConfig.module.route.detail(accountId) : moduleConfig.module.route.list}
                className={cn(
                  'inline-flex items-center justify-center w-9 h-9 rounded-lg border shrink-0 transition-colors',
                  colors.border,
                  colors.textMuted,
                  'hover:bg-black/5 dark:hover:bg-white/5',
                )}
                title="Back"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden />
              </Link>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className={cn('text-lg font-semibold truncate', colors.text)}>
                    {isEdit ? displayEmail : 'New email account'}
                  </h1>
                  <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', protocolBadgeClass(protocol))}>
                    {PROTOCOL_LABELS[protocol]}
                  </span>
                </div>
                <p className={cn('text-xs truncate', colors.textMuted)}>
                  Step {wizardStepIndex + 1} of {wizardSteps.length} · {WIZARD_STEP_LABELS[currentWizardStep]}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 sm:ml-auto">
              <Button
                type="button"
                onClick={() =>
                  navigate(isEdit && accountId ? moduleConfig.module.route.detail(accountId) : moduleConfig.module.route.list)
                }
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>

        <div className="w-full flex-1 min-h-0 px-4 py-5 sm:px-6 sm:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
            <div className={cn('rounded-xl border shadow-sm overflow-hidden flex flex-col', colors.border, colors.surface)}>
              <WizardStepper
                steps={wizardSteps}
                currentIndex={wizardStepIndex}
                onStepClick={setWizardStepIndex}
                colors={colors}
              />

              <div className="p-4 sm:p-6 flex-1 min-h-[22rem]">
                {currentWizardStep === 'basics' && (
                  <FieldGroup
                    title="Account details"
                    description="The email address used to sync and send mail."
                    colors={colors}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ea-email" required>
                          Email address
                        </Label>
                        <TextInput
                          id="ea-email"
                          type="email"
                          value={form.emailAddress ?? ''}
                          onChange={(e) => patchForm({ emailAddress: e.target.value })}
                          placeholder="user@company.com"
                          className="w-full mt-1"
                          autoComplete="off"
                          autoFocus
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-display">Display name</Label>
                        <TextInput
                          id="ea-display"
                          value={form.displayName ?? ''}
                          onChange={(e) => patchForm({ displayName: e.target.value })}
                          placeholder="Optional friendly name"
                          className="w-full mt-1"
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'protocol' && (
                  <FieldGroup
                    title="How should we connect?"
                    description="IMAP or OAuth2 keep folders in sync. POP3 downloads mail only."
                    colors={colors}
                  >
                    <ProtocolPicker value={protocol} onChange={setProtocol} colors={colors} />
                  </FieldGroup>
                )}

                {currentWizardStep === 'incoming' && protocol === 'oauth2' && (
                  <FieldGroup
                    title="OAuth2 & mail servers"
                    description="Connect Google or Microsoft, then confirm IMAP/SMTP hosts."
                    colors={colors}
                  >
                    <div className="space-y-4">
                      {isEdit && accountId > 0 ? (
                        <OAuthConnectSection
                          variant="embedded"
                          accountId={accountId}
                          oauthProvider={form.oauthProvider ?? account?.oauthProvider ?? null}
                          onConnected={() => void mutate()}
                        />
                      ) : (
                        <p className={cn('text-sm rounded-lg border px-3 py-2.5', colors.border, colors.textMuted)}>
                          Save the account first, then connect Google or Microsoft OAuth on the next visit.
                        </p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="ea-imap-host" required>
                            IMAP host
                          </Label>
                          <TextInput
                            id="ea-imap-host"
                            value={form.imapHost}
                            onChange={(e) => patchForm({ imapHost: e.target.value })}
                            placeholder="imap.gmail.com"
                            className="w-full mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="ea-imap-port">IMAP port</Label>
                          <TextInput
                            id="ea-imap-port"
                            type="number"
                            value={String(form.imapPort ?? 993)}
                            onChange={(e) => patchForm({ imapPort: parseInt(e.target.value, 10) || 993 })}
                            className="w-full mt-1"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <SwitchField
                            label="Use SSL/TLS for IMAP"
                            checked={form.imapSecure ?? true}
                            onChange={(checked) => patchForm({ imapSecure: checked })}
                            colors={colors}
                          />
                        </div>
                        <div>
                          <Label htmlFor="ea-smtp-host-oauth" required>
                            SMTP host
                          </Label>
                          <TextInput
                            id="ea-smtp-host-oauth"
                            value={form.smtpHost}
                            onChange={(e) => patchForm({ smtpHost: e.target.value })}
                            placeholder="smtp.gmail.com"
                            className="w-full mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="ea-smtp-port-oauth">SMTP port</Label>
                          <TextInput
                            id="ea-smtp-port-oauth"
                            type="number"
                            value={String(form.smtpPort ?? 587)}
                            onChange={(e) => patchForm({ smtpPort: parseInt(e.target.value, 10) || 587 })}
                            className="w-full mt-1"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <SwitchField
                            label="Use SSL/TLS for SMTP"
                            checked={form.smtpSecure ?? true}
                            onChange={(checked) => patchForm({ smtpSecure: checked })}
                            colors={colors}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label htmlFor="ea-username-oauth">SMTP username</Label>
                          <TextInput
                            id="ea-username-oauth"
                            value={form.username ?? ''}
                            onChange={(e) => patchForm({ username: e.target.value })}
                            placeholder="Usually your email address"
                            className="w-full mt-1"
                            ignoreAutofill
                          />
                        </div>
                        {showImapMirror && (
                          <div className="sm:col-span-2">
                            <Label htmlFor="ea-mirror-oauth">IMAP sync scope</Label>
                            <Select
                              id="ea-mirror-oauth"
                              value={form.imapMirrorMode ?? 'single_folder'}
                              onChange={(e) => patchForm({ imapMirrorMode: e.target.value })}
                              className="w-full mt-1"
                              options={[
                                { value: 'single_folder', label: 'INBOX only (legacy)' },
                                { value: 'full_mailbox', label: 'Full mailbox (all IMAP folders)' },
                              ]}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'incoming' && protocol === 'imap' && (
                  <FieldGroup title="IMAP settings" description="Incoming mail server credentials." colors={colors}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ea-imap-host" required>
                          IMAP host
                        </Label>
                        <TextInput
                          id="ea-imap-host"
                          value={form.imapHost}
                          onChange={(e) => patchForm({ imapHost: e.target.value })}
                          placeholder="imap.gmail.com"
                          className="w-full mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-imap-port">IMAP port</Label>
                        <TextInput
                          id="ea-imap-port"
                          type="number"
                          value={String(form.imapPort ?? 993)}
                          onChange={(e) => patchForm({ imapPort: parseInt(e.target.value, 10) || 993 })}
                          className="w-full mt-1"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <SwitchField
                          label="Use SSL/TLS for IMAP"
                          checked={form.imapSecure ?? true}
                          onChange={(checked) => patchForm({ imapSecure: checked })}
                          colors={colors}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-imap-user">IMAP username</Label>
                        <TextInput
                          id="ea-imap-user"
                          value={form.imapUsername ?? ''}
                          onChange={(e) => patchForm({ imapUsername: e.target.value })}
                          placeholder="Leave empty to use SMTP username"
                          className="w-full mt-1"
                          ignoreAutofill
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-imap-pass">IMAP password</Label>
                        <TextInput
                          id="ea-imap-pass"
                          type="password"
                          value={form.imapPassword ?? ''}
                          onChange={(e) => patchForm({ imapPassword: e.target.value })}
                          placeholder={isEdit ? 'Leave empty to keep current' : 'Optional if same as SMTP'}
                          className="w-full mt-1"
                          ignoreAutofill
                          showClearButton={false}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="ea-mirror-imap">IMAP sync scope</Label>
                        <Select
                          id="ea-mirror-imap"
                          value={form.imapMirrorMode ?? 'single_folder'}
                          onChange={(e) => patchForm({ imapMirrorMode: e.target.value })}
                          className="w-full mt-1"
                          options={[
                            { value: 'single_folder', label: 'INBOX only (legacy)' },
                            { value: 'full_mailbox', label: 'Full mailbox (all IMAP folders)' },
                          ]}
                        />
                      </div>
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'incoming' && protocol === 'pop3' && (
                  <FieldGroup title="POP3 settings" description="Incoming mail server for download-only sync." colors={colors}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ea-pop3-host" required>
                          POP3 host
                        </Label>
                        <TextInput
                          id="ea-pop3-host"
                          value={form.pop3Host ?? ''}
                          onChange={(e) => patchForm({ pop3Host: e.target.value })}
                          placeholder="pop.gmail.com"
                          className="w-full mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-pop3-port">POP3 port</Label>
                        <TextInput
                          id="ea-pop3-port"
                          type="number"
                          value={String(form.pop3Port ?? 995)}
                          onChange={(e) => patchForm({ pop3Port: parseInt(e.target.value, 10) || 995 })}
                          className="w-full mt-1"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <SwitchField
                          label="Use SSL/TLS for POP3"
                          checked={form.pop3Secure ?? true}
                          onChange={(checked) => patchForm({ pop3Secure: checked })}
                          colors={colors}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-pop3-user">POP3 username</Label>
                        <TextInput
                          id="ea-pop3-user"
                          value={form.pop3Username ?? ''}
                          onChange={(e) => patchForm({ pop3Username: e.target.value })}
                          placeholder="Leave empty to use SMTP username"
                          className="w-full mt-1"
                          ignoreAutofill
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-pop3-pass">POP3 password</Label>
                        <TextInput
                          id="ea-pop3-pass"
                          type="password"
                          value={form.pop3Password ?? ''}
                          onChange={(e) => patchForm({ pop3Password: e.target.value })}
                          placeholder={isEdit ? 'Leave empty to keep current' : 'Optional if same as SMTP'}
                          className="w-full mt-1"
                          ignoreAutofill
                          showClearButton={false}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <SwitchField
                          label="Delete from server after download"
                          checked={form.pop3DeleteAfterDownload ?? false}
                          onChange={(checked) => patchForm({ pop3DeleteAfterDownload: checked })}
                          colors={colors}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-pop3-retention">Retention on server (days)</Label>
                        <TextInput
                          id="ea-pop3-retention"
                          type="number"
                          min={0}
                          value={form.pop3RetentionDays != null ? String(form.pop3RetentionDays) : ''}
                          onChange={(e) =>
                            patchForm({
                              pop3RetentionDays: e.target.value === '' ? null : parseInt(e.target.value, 10),
                            })
                          }
                          placeholder="30"
                          className="w-full mt-1"
                        />
                      </div>
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'outgoing' && (
                  <FieldGroup title="SMTP settings" description="Outgoing mail server for sending messages." colors={colors}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ea-smtp-host" required>
                          SMTP host
                        </Label>
                        <TextInput
                          id="ea-smtp-host"
                          value={form.smtpHost}
                          onChange={(e) => patchForm({ smtpHost: e.target.value })}
                          placeholder="smtp.gmail.com"
                          className="w-full mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-smtp-port">SMTP port</Label>
                        <TextInput
                          id="ea-smtp-port"
                          type="number"
                          value={String(form.smtpPort ?? 587)}
                          onChange={(e) => patchForm({ smtpPort: parseInt(e.target.value, 10) || 587 })}
                          className="w-full mt-1"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <SwitchField
                          label="Use SSL/TLS for SMTP"
                          checked={form.smtpSecure ?? true}
                          onChange={(checked) => patchForm({ smtpSecure: checked })}
                          colors={colors}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-smtp-user" required>
                          SMTP username
                        </Label>
                        <TextInput
                          id="ea-smtp-user"
                          value={form.username ?? ''}
                          onChange={(e) => patchForm({ username: e.target.value })}
                          className="w-full mt-1"
                          ignoreAutofill
                        />
                      </div>
                      <div>
                        <Label htmlFor="ea-smtp-pass">SMTP password</Label>
                        <TextInput
                          id="ea-smtp-pass"
                          type="password"
                          value={form.password ?? ''}
                          onChange={(e) => patchForm({ password: e.target.value })}
                          placeholder={isEdit ? 'Leave empty to keep current' : 'Enter SMTP password'}
                          className="w-full mt-1"
                          ignoreAutofill
                          showClearButton={false}
                        />
                      </div>
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'sync' && (
                  <FieldGroup
                    title="Sync settings"
                    description="Control automatic background synchronization."
                    colors={colors}
                  >
                    <div className="space-y-3">
                      <SwitchField
                        label="Enable automatic email synchronization"
                        description="Automatically sync emails from this account in the background."
                        checked={form.syncEnabled ?? true}
                        onChange={(checked) => patchForm({ syncEnabled: checked })}
                        colors={colors}
                      />
                      {form.syncEnabled && (
                        <div className="max-w-xs">
                          <Label htmlFor="ea-sync-interval">Sync interval (minutes)</Label>
                          <TextInput
                            id="ea-sync-interval"
                            type="number"
                            min={1}
                            max={1440}
                            value={String(form.syncInterval ?? 15)}
                            onChange={(e) => patchForm({ syncInterval: parseInt(e.target.value, 10) || 15 })}
                            className="w-full mt-1"
                          />
                        </div>
                      )}
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'sidebar' && (
                  <FieldGroup
                    title="Emails sidebar"
                    description="Defaults for how this account appears on /emails."
                    colors={colors}
                  >
                    <div className="space-y-3">
                      <SwitchField
                        label="Show in Accounts sidebar"
                        description="When shared, users see this mailbox under Accounts in /emails."
                        checked={form.showInAccountsSidebar ?? true}
                        onChange={(checked) => patchForm({ showInAccountsSidebar: checked })}
                        colors={colors}
                      />
                      <SwitchField
                        label="Create smart folder inbox"
                        description="Adds an INBOX entry under Smart Folders (auto-created on save)."
                        checked={form.showSearchFolderInbox ?? false}
                        onChange={(checked) => patchForm({ showSearchFolderInbox: checked })}
                        colors={colors}
                      />
                      {isEdit && accountId > 0 && (
                        <div className={cn('rounded-lg border p-4 space-y-2', colors.border)}>
                          <p className={cn('text-sm font-medium', colors.text)}>Sent / My sent — From addresses</p>
                          <p className={cn('text-xs', colors.textMuted)}>
                            Choose which send-as addresses appear in Sent and My sent.
                          </p>
                          <SentFolderIdentitiesSection
                            accountId={accountId}
                            accountEmail={form.emailAddress}
                            sentFolderIdentityIds={form.sentFolderIdentityIds ?? account?.sentFolderIdentityIds ?? null}
                            onMutate={() => void mutate()}
                          />
                        </div>
                      )}
                    </div>
                  </FieldGroup>
                )}

                {currentWizardStep === 'sharing' && (
                  <FieldGroup
                    title="Sharing"
                    description="Who can access this mailbox and its send identities."
                    colors={colors}
                  >
                    {sharingAccount ? (
                      <AccountSharesSection
                        account={sharingAccount}
                        onMutate={() => {
                          void mutate();
                          if (createdAccount) {
                            void globalMutate((key: unknown) => typeof key === 'string' && key.startsWith('/email-accounts'));
                          }
                        }}
                      />
                    ) : (
                      <p className={cn('text-sm rounded-lg border px-3 py-2.5', colors.border, colors.textMuted)}>
                        Click <strong>Create</strong> below to save this account, then add users who should access it.
                      </p>
                    )}
                  </FieldGroup>
                )}
              </div>

              <div
                className={cn(
                  'flex items-center justify-between gap-3 border-t px-4 py-4 sm:px-6',
                  colors.border,
                  colors.surfaceHover,
                )}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={goWizardBack}
                  disabled={wizardStepIndex === 0}
                  className="gap-1"
                >
                  <ChevronLeft className="w-4 h-4" aria-hidden />
                  Back
                </Button>
                {isLastWizardStep ? (
                  <Button
                    key="wizard-save"
                    type="button"
                    disabled={saving || !validateWizardStep('basics', form)}
                    loading={saving}
                    size="sm"
                    onClick={() => void saveAccount()}
                  >
                    {finishButtonLabel}
                  </Button>
                ) : (
                  <Button
                    key="wizard-next"
                    type="button"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      goWizardNext();
                    }}
                    disabled={!canProceedWizard}
                    className="gap-1"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" aria-hidden />
                  </Button>
                )}
              </div>
            </div>

            <aside className="lg:sticky lg:top-[4.5rem] space-y-4">
              <div className={cn('rounded-xl border overflow-hidden shadow-sm', colors.border, colors.surface)}>
                <div className={cn('px-4 py-2.5 border-b text-[10px] font-semibold uppercase tracking-wider', colors.border, colors.textMuted)}>
                  Preview
                </div>
                <div className="p-4 space-y-3">
                  <div className={cn('rounded-lg p-2.5', 'bg-gray-100/80 dark:bg-gray-900/60')}>
                    <p className={cn('text-[10px] font-medium uppercase tracking-wide mb-2 px-1', colors.textMuted)}>
                      Accounts
                    </p>
                    <div
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium',
                        'bg-sky-600/10 text-sky-900 dark:bg-sky-500/15 dark:text-sky-100 ring-1 ring-sky-500/20',
                      )}
                    >
                      <span className="flex items-center justify-center w-6 h-6 rounded-md shrink-0 bg-sky-600 text-white text-xs font-bold">
                        {(displayEmail[0] ?? '?').toUpperCase()}
                      </span>
                      <span className="min-w-0 truncate">
                        {displayName ? `${displayName} · ${displayEmail}` : displayEmail}
                      </span>
                    </div>
                  </div>

                  <dl className={cn('space-y-2 text-xs', colors.textSecondary)}>
                    <div className="flex justify-between gap-2">
                      <dt className={colors.textMuted}>Protocol</dt>
                      <dd className={cn('font-medium', colors.text)}>{PROTOCOL_LABELS[protocol]}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className={colors.textMuted}>Auto sync</dt>
                      <dd className={cn('font-medium', colors.text)}>{form.syncEnabled ? `${form.syncInterval ?? 15} min` : 'Off'}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className={colors.textMuted}>In sidebar</dt>
                      <dd className={cn('font-medium', colors.text)}>{form.showInAccountsSidebar ? 'Yes' : 'No'}</dd>
                    </div>
                    {currentWizardStep === 'sharing' && (
                      <div className="flex justify-between gap-2">
                        <dt className={colors.textMuted}>Sharing</dt>
                        <dd className={cn('font-medium', colors.text)}>
                          {sharingAccount ? 'Configure below' : 'After create'}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Form;
